import { LocationService } from '@weave/schema-gen-ts/dist/schemas/location/v2/data-location/data_location.pb';
import { authorizedFetch } from '../../../../helpers/utils';
import { CustomAxios, getResponseData } from '../../../axios';
import {
  LocationCreationResponseModel,
  ProvisionLocationRequestDTO,
  ProvisionLocationResponseDTO,
} from '../location-status';
import {
  convertLocationToProvisionLocationDTO,
  CreateLocationDTO,
  LocationModel,
  LocationSummary,
  OfficeInfo,
  stripLocationEntities,
} from './locations.types';

export const getLocation = (locationId: string): Promise<LocationModel> =>
  CustomAxios.get(`support/v1/locations/${locationId}`).then(getResponseData);

export const getLocationSummary = (locationId: string): Promise<LocationSummary> =>
  CustomAxios.get(`support/v1/locations/${locationId}/summary`).then(getResponseData);

export const provisionLocation = (
  location: CreateLocationDTO
): Promise<LocationCreationResponseModel> => {
  const provisionData = convertLocationToProvisionLocationDTO(location);
  return CustomAxios.post(`support/v1/initialprovision`, provisionData).then(
    getResponseData
  );
};

export const newProvisionLocation = (
  location: ProvisionLocationRequestDTO
): Promise<ProvisionLocationResponseDTO> => {
  return CustomAxios.post(`location/v1/provision/wam`, location)
    .then(getResponseData)
    .then(normalizeProvisionLocationResponse);
};

type LocationId = string;
export const createLocation = (location: ProvisionLocationRequestDTO) => {
  return CustomAxios.post(`support/v1/locations`, location).then<LocationId>(
    getResponseData
  );
};

export const updateLocation = (
  locationId: string,
  location: LocationModel
): Promise<null> =>
  CustomAxios.put(
    `support/v1/locations/${locationId}`,
    stripLocationEntities(location)
  ).then(getResponseData);

export const setLocationParent = (
  locationId: string,
  parentLocationId: string
): Promise<null> =>
  CustomAxios.put(
    `support/v1/locations/${locationId}/parent`,
    parentLocationId ? { ParentID: parentLocationId } : {}
  ).then(getResponseData);

export const setPhoneTenantId = (
  locationId: string,
  phoneTenanatId: string
): Promise<null> =>
  CustomAxios.put(
    `support/v1/locations/${locationId}/phone-tenant`,
    phoneTenanatId ? { PhoneTenantID: phoneTenanatId } : {}
  ).then(getResponseData);

export const updateOfficeInfo = (info: OfficeInfo): Promise<boolean> =>
  CustomAxios.post(`support/v1/officeinfo`, info).then(getResponseData);

export const getLocationV2DetailById = (locationId: string) =>
  LocationService.GetLocation(authorizedFetch(), { locationId });

export const getChildrenLocations = (
  parentLocationId: string
): Promise<LocationModel[]> =>
  CustomAxios.get(`support/v1/locations/${parentLocationId}/children`).then(
    getResponseData
  );

type RawProvisionLocationResponse = Partial<ProvisionLocationResponseDTO> & {
  location_created_successfully?: boolean;
  phone_data_tenant_created_successfully?: boolean;
  customization_flags_set_successfully?: boolean;
  salesforce_updated_successfully?: boolean;
  location_id?: string;
  tenant_id?: string;
  location_provision_error?: string;
  temp_phone_number?: string;
};

const normalizeProvisionLocationResponse = (
  response: RawProvisionLocationResponse
): ProvisionLocationResponseDTO => ({
  locationCreatedSuccessfully:
    response.locationCreatedSuccessfully ??
    response.location_created_successfully ??
    false,
  phoneDataTenantCreatedSuccessfully:
    response.phoneDataTenantCreatedSuccessfully ??
    response.phone_data_tenant_created_successfully ??
    false,
  customizationFlagsSetSuccessfully:
    response.customizationFlagsSetSuccessfully ??
    response.customization_flags_set_successfully ??
    false,
  salesforceUpdatedSuccessfully:
    response.salesforceUpdatedSuccessfully ??
    response.salesforce_updated_successfully ??
    false,
  locationId: response.locationId ?? response.location_id ?? '',
  tenantId: response.tenantId ?? response.tenant_id ?? '',
  locationProvisionError:
    response.locationProvisionError ?? response.location_provision_error ?? '',
  creationStillProcessing: response.creationStillProcessing,
  phoneTenantCreationRequested: response.phoneTenantCreationRequested,
  tempPhoneNumber: response.tempPhoneNumber ?? response.temp_phone_number,
});
